import styles from './im-sidebar.module.scss';

import React, {useEffect} from 'react';
import {get} from 'lodash/object';
import {Link, NavLink} from 'react-router-dom';

import {useAuth} from 'contexts/AuthContext';
import {ACCOUNT_ROLES, COMPANY_TYPES, COMPANY_STATUSES} from 'configs';
import classNames from 'classnames';
import {isEmpty} from 'lodash';
import Cookies from 'utils/cookies';
import {getFormattedNumber} from 'utils/numbers';
import {useGlobalCounts} from 'contexts/GlobalCountContext';
import {Badge, Label} from 'reactstrap';
import {ImageLogo} from 'components/Images';
import {useMembership} from 'contexts/MembershipContext';
import MuiIcon from 'components/Icons/MuiIcon';
import {
  ENABLE_ANALYTICS,
  ENABLE_IMPORT_PREVIEW,
  ENABLE_ISO_SURVEY,
} from 'configs/enable-features';
import {SUBSCRIPTION_TYPE_LEVEL} from 'configs/membership';

function PrivateSidebarNavsItem(props) {
  const {nav} = props;
  const {to, content, className, data, icon, isTry} = nav; //isNew

  return (
    <li>
      <NavLink to={to}>
        <div
          className={classNames(
            'd-flex align-items-center flex-row',
            className
          )}
        >
          <div className="flex-grow-1 d-flex align-items-center">
            {icon && (
              <div>
                <MuiIcon name={icon} className="mr-3" size="lg" />
              </div>
            )}
            <p className="m-0">{content}</p>
          </div>
          <div>
            <Label
              className={classNames('mb-0 font-weight-normal', {
                'd-none': !data,
              })}
              style={{textDecoration: 'none'}}
            >
              {getFormattedNumber(data)}
            </Label>
          </div>
          {isTry && (
            <div className="d-block">
              <Badge color="success" className="border border-white">
                <div>
                  <MuiIcon
                    name="diamond"
                    variant="outlined"
                    className="mr-1"
                    style={{fontSize: '14px'}}
                  />
                  <span>TRY</span>
                </div>
              </Badge>
            </div>
          )}
        </div>
      </NavLink>
    </li>
  );
}

function PrivateSidebarNavs(props) {
  const {value, hr = true} = props;
  const {header, navs} = value;

  if (isEmpty(navs)) {
    return false;
  }

  return (
    <>
      <div className="my-3">
        {!!header && <small>{header}</small>}
        <ul>
          {(navs || []).map((nav, index) => (
            <PrivateSidebarNavsItem key={index} nav={nav} />
          ))}
        </ul>
      </div>
      {!!hr && <hr />}
    </>
  );
}

function PrivateSidebar() {
  const auth = useAuth();
  const {
    isSubscribed,
    hasPermit,
    isSuspendedSubscription,
    isSubscribedToMI,
    // isLegacyUser,
    isTrial,
    subscriptionLevel,
  } = useMembership();
  const {globalCounts, getGlobalCounts} = useGlobalCounts();
  const companyType = get(auth, 'account.company.company_type', '');
  const isCE = companyType === COMPANY_TYPES.ce;
  const isCompany = get(auth, 'isCompany');

  const isAdmin = get(auth, 'account.account_role') === ACCOUNT_ROLES.admin;
  const isStaff = get(auth, 'account.account_role') === ACCOUNT_ROLES.staff;
  const isSuperAdmin =
    get(auth, 'account.account_role') === ACCOUNT_ROLES.super_admin;
  const isCompanySuspended =
    get(auth, 'account.company.company_status') === COMPANY_STATUSES.suspended;
  // const isEnterprise =
  //   subscriptionLevel === SUBSCRIPTION_TYPE_LEVEL['enterprise'];
  const isFree = subscriptionLevel === SUBSCRIPTION_TYPE_LEVEL['free'];
  // const showMarketIntelligence =
  //   isFree || (!isLegacyUser && !isTrial) || isEnterprise;

  const {certifications_count, companies_count, watchlist_count} =
    globalCounts || {};

  const isVuAccount = isCE || isCompany;
  const isFetchingSession = get(auth, 'isFetchingSession');

  useEffect(() => {
    if (isVuAccount && !isSuspendedSubscription && !isFetchingSession) {
      getGlobalCounts();
    }
  }, [isVuAccount, isSuspendedSubscription, isFetchingSession]);

  const overviews = {
    header: 'Overview',
    navs: [
      {
        to: '/dashboard',
        content: 'Dashboard',
      },
      isCE && {
        to: '/certifications',
        content: 'My Certifications',
      },
      ENABLE_ANALYTICS &&
        ENABLE_ISO_SURVEY &&
        !isSuspendedSubscription &&
        !isCompanySuspended && {
          to: isSubscribedToMI
            ? '/analytics/certifications'
            : '/analytics/iso-survey',
          content: (
            <span className="text-nowrap">
              Market Intelligence
              <Badge
                color="danger"
                className="ml-2 align-text-top"
                style={{padding: '0.35em 0.4em 0.25em 0.4em'}}
              >
                ISO SURVEY
              </Badge>
            </span>
          ),
          isNew: true,
        },
    ].filter((v) => v),
  };

  const search = {
    header: 'Company Search',
    navs: [
      {
        to: '/advanced-search',
        content: 'Search',
        icon: 'search',
      },
    ],
  };

  const data = {
    header: 'Listing',
    navs: [
      {
        isNew: true,
        to: '/company-list',
        content: 'Companies',
        className: 'onboard-companies-tab',
        data: companies_count,
      },
      {
        isNew: true,
        to: '/certifications-list',
        content: 'Certifications',
        className: 'onboard-certs-tab',
        data: certifications_count,
      },
      (isSubscribed || (!isSubscribed && isSuperAdmin)) && {
        to: '/watch-list',
        content: 'Watchlist',
        isNew: isCE,
        data: watchlist_count,
      },
    ].filter((v) => v),
  };

  const verifications = {
    header: 'Bulk Verification',
    navs: [
      ((isStaff && isSubscribed) ||
        (isAdmin && isSubscribed) ||
        isSuperAdmin) && {
        isNew: true,
        to: '/import-management',
        content: 'Import Company Data',
        className: 'onboard-import-tab',
        isTry: (isFree || isTrial) && ENABLE_IMPORT_PREVIEW,
      },
      (isSubscribed || isTrial || ENABLE_IMPORT_PREVIEW) && {
        isNew: true,
        to: '/import-settings',
        content: 'Import Settings',
        className: 'onboard-import-settings-tab',
      },
      hasPermit('master_api_key.access') && {
        isNew: true,
        to: '/api-verification',
        content: 'API Settings',
      },
    ].filter((v) => v),
  };

  const message = {
    header: 'Messages',
    navs: [
      {
        to: '/enquiries',
        content: 'Enquiries',
      },
    ],
  };

  const history = {
    header: 'History',
    navs: [
      {
        to: '/search-history',
        content: 'Search History',
        isNew: isCE,
      },
    ],
  };

  const administration = {
    header: 'Administration',
    navs: [
      isSuperAdmin && {to: '/users', content: 'Users'},
      isCE && {to: '/ce-profile', content: 'Company Profile'},
      isCompany && {
        to: '/company-profile',
        content: 'Company Profile',
      },
      isSuperAdmin && {
        isNew: true,
        to: '/membership',
        content: 'Membership & Billing',
        className: 'onboard-membership-tab',
      },

      {to: '/settings', content: 'Settings'},
    ].filter((v) => v),
  };

  const navigation = (
    <>
      <PrivateSidebarNavs value={overviews} />
      <PrivateSidebarNavs value={search} />
      <PrivateSidebarNavs value={data} />
      {!isEmpty(verifications) && <PrivateSidebarNavs value={verifications} />}
      <PrivateSidebarNavs value={message} />
      <PrivateSidebarNavs value={history} />
      <PrivateSidebarNavs value={administration} hr={false} />
    </>
  );

  const navigationSuspended = (
    <>
      <PrivateSidebarNavs value={overviews} />
      <PrivateSidebarNavs value={message} />
      <PrivateSidebarNavs value={administration} hr={false} />
    </>
  );

  return (
    <nav
      className={classNames(styles['private-sidebar'], {
        [styles['private-sidebar-height']]: !Cookies.consent,
      })}
    >
      <div
        className={classNames('border-bottom', styles['private-sidebar--logo'])}
      >
        <Link to="/">
          <ImageLogo size="lg" />
        </Link>
      </div>
      {isCompanySuspended || isSuspendedSubscription
        ? navigationSuspended
        : navigation}
    </nav>
  );
}

export default PrivateSidebar;
